import React, { useRef, useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    IconButton,
    Rating,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Card,
    CardContent,
    CardActions,
    Fab,
    Tooltip,
    Stack,
    Fade
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { isAdminMode } from '../utils/auth'; // Import from utility file

interface Testimonial {
    id: number;
    name: string;
    quote: string;
    rating: number;
    designation: string;
}

interface CreateTestimonialDto {
    name: string;
    quote: string;
    rating: number;
    designation: string;
}

interface UpdateTestimonialDto {
    name?: string;
    quote?: string;
    rating?: number;
    designation?: string;
}

const TestimonialsSection: React.FC = () => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [adminMode, setAdminMode] = useState<boolean>(false);

    // Dialog states
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);

    // Form states
    const [formData, setFormData] = useState<CreateTestimonialDto>({
        name: '',
        quote: '',
        rating: 0,
        designation: ''
    });

    // API configuration
    const BASE_URL = import.meta.env.VITE_BASE_URL ?? '';

    useEffect(() => {
        fetchTestimonials();
        setAdminMode(isAdminMode());
    }, []);

    const fetchTestimonials = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${BASE_URL}/api/testimonials`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setTestimonials(data);
        } catch (err) {
            console.error('Error fetching testimonials:', err);
            setError('Failed to load testimonials. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTestimonial = async () => {
        try {
            const response = await fetch(`${BASE_URL}/api/testimonials`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            await fetchTestimonials(); // Refresh the list
            handleCloseDialog();
        } catch (err) {
            console.error('Error creating testimonial:', err);
            setError('Failed to create testimonial. Please try again.');
        }
    };

    const handleUpdateTestimonial = async (id: number) => {
        try {
            const updateData: UpdateTestimonialDto = { ...formData };

            const response = await fetch(`${BASE_URL}/api/testimonials/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updateData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            await fetchTestimonials(); // Refresh the list
            handleCloseDialog();
        } catch (err) {
            console.error('Error updating testimonial:', err);
            setError('Failed to update testimonial. Please try again.');
        }
    };

    const handleDeleteTestimonial = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this testimonial?')) {
            return;
        }

        try {
            const response = await fetch(`${BASE_URL}/api/testimonials/${id}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            await fetchTestimonials(); // Refresh the list
        } catch (err) {
            console.error('Error deleting testimonial:', err);
            setError('Failed to delete testimonial. Please try again.');
        }
    };

    const handleOpenCreateDialog = () => {
        setDialogMode('create');
        setFormData({
            name: '',
            quote: '',
            rating: 0,
            designation: ''
        });
        setOpenDialog(true);
    };

    const handleOpenEditDialog = (testimonial: Testimonial) => {
        setDialogMode('edit');
        setEditingTestimonial(testimonial);
        setFormData({
            name: testimonial.name,
            quote: testimonial.quote,
            rating: testimonial.rating,
            designation: testimonial.designation
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingTestimonial(null);
        setFormData({
            name: '',
            quote: '',
            rating: 0,
            designation: ''
        });
    };

    const handleFormSubmit = () => {
        if (dialogMode === 'create') {
            handleCreateTestimonial();
        } else if (editingTestimonial) {
            handleUpdateTestimonial(editingTestimonial.id);
        }
    };

    const handleInputChange = (field: keyof CreateTestimonialDto, value: string | number) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({
                left: direction === 'left' ? -300 : 300,
                behavior: 'smooth'
            });
        }
    };

    if (loading) {
        return (
            <Box className="relative py-12 px-4 sm:px-10 flex justify-center items-center">
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box className="relative py-12 px-4 sm:px-10">
                <Alert severity="error" action={
                    <Button color="inherit" size="small" onClick={fetchTestimonials}>
                        Retry
                    </Button>
                }>
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <div className="max-w-full p-6">
            <div className="w-full mx-auto">
                {/* Header */}
                <Fade in timeout={800}>
                    <Box className="text-center mb-12">
                        <Typography
                            variant="h2"
                            sx={{
                                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                                fontWeight: 800,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                mb: 3,
                                letterSpacing: '-0.02em'
                            }}
                        >
                            Testimonials
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'text.secondary',
                                fontSize: '1.1rem',
                                maxWidth: '600px',
                                mx: 'auto',
                                lineHeight: 1.6,
                                fontWeight: 400
                            }}
                        >
                            Here you can add testimonials and view the testimonials.
                        </Typography>
                    </Box>
                </Fade>

                {/* Admin Mode - Add Button */}
                {adminMode && (
                    <Box className="mb-4 flex justify-end">
                        <Fab
                            color="primary"
                            aria-label="add testimonial"
                            size="medium"
                            onClick={handleOpenCreateDialog}
                        >
                            <AddIcon />
                        </Fab>
                    </Box>
                )}

                {testimonials.length === 0 ? (
                    <Box className="text-center py-8">
                        <Typography variant="body1" color="textSecondary">
                            No testimonials available at the moment.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Admin Mode - Cards in Flexbox Layout */}
                        {adminMode ? (
                            <Box className="mb-8">
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 3,
                                        justifyContent: 'flex-start',
                                        alignItems: 'stretch'
                                    }}
                                >
                                    {testimonials.map((testimony) => (
                                        <Card
                                            key={testimony.id}
                                            elevation={3}
                                            sx={{
                                                width: { xs: '100%', sm: 'calc(50% - 12px)', lg: 'calc(33.333% - 16px)' },
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                        >
                                            <CardContent sx={{ flexGrow: 1 }}>
                                                <Typography variant="h6" gutterBottom>
                                                    {testimony.name}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    {testimony.designation}
                                                </Typography>
                                                <Typography variant="body2" className="mb-2 line-clamp-3">
                                                    "{testimony.quote}"
                                                </Typography>
                                                <Box className="flex items-center">
                                                    <Rating
                                                        value={testimony.rating}
                                                        readOnly
                                                        size="small"
                                                    />
                                                    <Typography variant="caption" className="ml-2">
                                                        ({testimony.rating})
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                            <CardActions>
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleOpenEditDialog(testimony)}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteTestimonial(testimony.id)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </CardActions>
                                        </Card>
                                    ))}
                                </Box>
                            </Box>
                        ) : (
                            /* Public Mode - Grid Layout with 2 Cards, styled like the image */
                            <Box className="flex flex-col items-center justify-center">
                                <Box className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-4xl mx-auto">
                                    {testimonials.slice(0, 2).map((testimony) => (
                                        <Paper
                                            key={testimony.id}
                                            elevation={3}
                                            className="rounded-2xl bg-white shadow-lg flex flex-col items-center p-8"
                                            sx={{ minHeight: 320 }}
                                        >
                                            {/* Avatar/Initials Placeholder */}
                                            <Box className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center mb-6 text-3xl font-bold text-indigo-500">
                                                {testimony.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                            </Box>
                                            <Typography
                                                variant="body1"
                                                className="text-gray-700 mb-6 text-center italic"
                                                sx={{ minHeight: '72px', fontSize: '1.1rem' }}
                                            >
                                                "{testimony.quote}"
                                            </Typography>
                                            <Box className="flex flex-col items-center mt-auto">
                                                <Typography
                                                    variant="h6"
                                                    className="font-bold text-lg mt-2"
                                                    color="textPrimary"
                                                >
                                                    {testimony.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    className="mb-2"
                                                >
                                                    {testimony.designation}
                                                </Typography>
                                                <Rating
                                                    value={testimony.rating}
                                                    readOnly
                                                    size="small"
                                                    sx={{ color: '#FFD700' }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                    className="ml-1"
                                                >
                                                    ({testimony.rating})
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </>
                )}

                {/* Create/Edit Dialog */}
                <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                    <DialogTitle>
                        {dialogMode === 'create' ? 'Add New Testimonial' : 'Edit Testimonial'}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        value={formData.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        required
                                    />
                                    <TextField
                                        fullWidth
                                        label="Designation"
                                        value={formData.designation}
                                        onChange={(e) => handleInputChange('designation', e.target.value)}
                                        required
                                    />
                                </Box>
                                <TextField
                                    fullWidth
                                    label="Quote"
                                    multiline
                                    rows={4}
                                    value={formData.quote}
                                    onChange={(e) => handleInputChange('quote', e.target.value)}
                                    required
                                />
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>

                                    <Box sx={{ minWidth: 120 }}>
                                        <Typography component="legend">Rating</Typography>
                                        <Rating
                                            value={formData.rating}
                                            onChange={(_, newValue) => handleInputChange('rating', newValue || 0)}
                                            precision={0.5}
                                        />
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleFormSubmit}
                            variant="contained"
                            startIcon={<SaveIcon />}
                        >
                            {dialogMode === 'create' ? 'Create' : 'Update'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </div>
    );
};

export default TestimonialsSection;